import React from "react";
import styled from "styled-components";
import { Card, Statistic, Skeleton, Tooltip, Button, Modal } from "antd";
import {
  UserAddOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import CopyField from "../common/CopyField";
import { getReferralSummary } from "../../lib/firebase/referralModel";

const ReferralSummaryCard = ({ loading = false, data = null }) => {
  const [rewardsInfoVisible, setRewardsInfoVisible] = React.useState(false);

  // TODO(copy): rewards copy is centralized for easy changes
  const rewardsInfo = {
    friendReward:
      "The referred landlord gets a 1-month free trial",
    yourReward:
      "You get 1 free month (or cash bonus if enabled) after they pay their first month",
  };

  if (loading) {
    return (
      <SummaryCard>
        <CardHeader>
          <Skeleton.Input active size="large" style={{ width: 200 }} />
        </CardHeader>
        <Skeleton active paragraph={{ rows: 4 }} />
      </SummaryCard>
    );
  }

  const referralData = data || {
    code: "USER",
    link: `${window.location.origin}/signup?ref=USER`,
    totals: { pending: 0, active: 0, total: 0 },
    rewards: { freeMonths: 0, cashCents: 0 },
  };

  return (
    <SummaryCard>
      <CardHeader>
        <CardTitle>Your Landlord Referral Program</CardTitle>
        <Tooltip title="How rewards work">
          <RewardsInfoButton
            type="text"
            icon={<QuestionCircleOutlined />}
            onClick={() => setRewardsInfoVisible(true)}
          />
        </Tooltip>
      </CardHeader>

      <ReferralCodeSection>
        <CodeLabel>Your Referral Code</CodeLabel>
        <CopyField value={referralData.code} placeholder="Your referral code" />
      </ReferralCodeSection>

      <ReferralLinkSection>
        <LinkLabel>Your Referral Link</LinkLabel>
        <CopyField value={referralData.link} placeholder="Your referral link" />
      </ReferralLinkSection>

      <StatsGrid>
        <Statistic
          title="Total Referrals"
          value={referralData.totals.total}
          prefix={<UserAddOutlined />}
        />
        <Statistic
          title="Active Users"
          value={referralData.totals.active}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: "#52c41a" }}
        />
        <Statistic
          title="Pending"
          value={referralData.totals.pending}
          prefix={<ClockCircleOutlined />}
          valueStyle={{ color: "#faad14" }}
        />
        <Statistic
          title="Free Months Earned"
          value={referralData.rewards.freeMonths}
          prefix={<GiftOutlined />}
          valueStyle={{ color: "#1890ff" }}
        />
      </StatsGrid>

      {/* Rewards Info Modal */}
      <Modal
        title="How Landlord Referral Rewards Work"
        open={rewardsInfoVisible}
        onCancel={() => setRewardsInfoVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRewardsInfoVisible(false)}>
            Got it
          </Button>,
        ]}
      >
        <RewardsInfoContent>
          <RewardItem>
            <RewardIcon>🎁</RewardIcon>
            <RewardText>{rewardsInfo.friendReward}</RewardText>
          </RewardItem>
          <RewardItem>
            <RewardIcon>💰</RewardIcon>
            <RewardText>{rewardsInfo.yourReward}</RewardText>
          </RewardItem>
        </RewardsInfoContent>
      </Modal>
    </SummaryCard>
  );
};

const SummaryCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
`;

const RewardsInfoButton = styled(Button)`
  color: var(--color-text-light);

  &:hover {
    color: var(--color-primary);
  }
`;

const ReferralCodeSection = styled.div`
  margin-bottom: 20px;
`;

const ReferralLinkSection = styled.div`
  margin-bottom: 24px;
`;

const CodeLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--color-text);
`;

const LinkLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--color-text);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: 14px;
    color: var(--color-text-light);
  }

  .ant-statistic-content {
    font-size: 24px;
    font-weight: 600;
  }
`;

const RewardsInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--color-bg-secondary);
  border-radius: 8px;
`;

const RewardIcon = styled.div`
  font-size: 24px;
`;

const RewardText = styled.div`
  font-size: 16px;
  color: var(--color-text);
  line-height: 1.5;
`;

export default ReferralSummaryCard;
